import { Eye, MapPin, Smartphone, Monitor, Code } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button,
} from '../ui'
import type { AttendanceEvent } from '../../types/attendance.types'
import { formatDateTime } from '../../utils/dateFormat'

interface AttendanceTableProps {
  events: AttendanceEvent[]
  onViewDetails: (event: AttendanceEvent) => void
}

export function AttendanceTable({ events, onViewDetails }: AttendanceTableProps) {
  const getEventTypeBadge = (eventType: string) => {
    switch (eventType) {
      case 'check_in':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Приход
          </Badge>
        )
      case 'check_out':
        return (
          <Badge variant="default" className="bg-red-100 text-red-800">
            Уход
          </Badge>
        )
      case 'break_start':
        return (
          <Badge variant="default" className="bg-yellow-100 text-yellow-800">
            Начало перерыва
          </Badge>
        )
      case 'break_end':
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-800">
            Конец перерыва
          </Badge>
        )
      default:
        return <Badge variant="secondary">{eventType}</Badge>
    }
  }

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />
      case 'terminal':
        return <Monitor className="h-4 w-4" />
      case 'api':
        return <Code className="h-4 w-4" />
      default:
        return null
    }
  }

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'mobile':
        return 'Мобильное'
      case 'terminal':
        return 'Терминал'
      case 'api':
        return 'API'
      default:
        return sourceType
    }
  }


  const getEmployeeName = (event: AttendanceEvent) => {
    if (!event.employee) return 'Не указан'
    const { first_name, last_name, middle_name } = event.employee
    return [last_name, first_name, middle_name].filter(Boolean).join(' ')
  }

  const hasGPSData = (event: AttendanceEvent) => {
    return event.lat && event.lng
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Дата и время</TableHead>
          <TableHead>Сотрудник</TableHead>
          <TableHead>Тип события</TableHead>
          <TableHead>Локация</TableHead>
          <TableHead>Источник</TableHead>
          <TableHead className="text-right">Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((event) => {
          const dateTime = formatDateTime(event.occurred_at)
          return (
            <TableRow key={event.id}>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">{dateTime.date}</p>
                  <p className="text-sm text-muted-foreground">{dateTime.time}</p>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">{getEmployeeName(event)}</p>
                  {event.employee && (
                    <div className="flex flex-col text-sm text-muted-foreground">
                      {event.employee.employee_number && (
                        <span>№{event.employee.employee_number}</span>
                      )}
                      {event.employee.department && (
                        <span>{event.employee.department.name}</span>
                      )}
                      {event.employee.position && (
                        <span className="text-xs">{event.employee.position.name}</span>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>{getEventTypeBadge(event.event_type)}</TableCell>
              <TableCell>
                {event.location ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <p className="font-medium">{event.location.name}</p>
                    </div>
                    {event.location.address && (
                      <p className="text-xs text-muted-foreground">{event.location.address}</p>
                    )}
                    {hasGPSData(event) && (
                      <p className="text-xs text-muted-foreground">
                        GPS: {event.lat?.toFixed(6)}, {event.lng?.toFixed(6)}
                        {event.accuracy && ` (±${event.accuracy}м)`}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">Не указана</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getSourceIcon(event.source_type)}
                  <span className="text-sm">{getSourceLabel(event.source_type)}</span>
                </div>
                {event.validation_method && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Метод: {event.validation_method}
                  </p>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  title="Просмотр деталей"
                  onClick={() => onViewDetails(event)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}